import { Share2, Shield, Link2, Copy, Trash2, X } from 'lucide-react';

export function ShareLinkModal({
  show, onClose, shareLinks, newLink, onNewLinkChange, creatingLink, createdLink,
  copiedCode, onCreateLink, onRevokeLink, onCopyToClipboard, getPortalUrl,
}) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="share-modal-overlay">
      <div className="bg-card border border-border rounded-sm w-full max-w-lg mx-4 animate-fade-in max-h-[80vh] flex flex-col" data-testid="share-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-400" />
            <span className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">Client Delivery Portal</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors" data-testid="close-share-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-auto">
          {/* Just-created link display */}
          {createdLink && (
            <div className="bg-emerald-950/20 border border-emerald-800/50 rounded-sm p-4 mb-5 animate-fade-in" data-testid="created-link-info">
              <p className="font-heading font-bold text-xs uppercase tracking-wider text-emerald-400 mb-3">Link Created</p>
              <div className="space-y-3">
                <div>
                  <label className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Portal URL</label>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code className="flex-1 bg-card border border-border rounded-sm px-2 py-1.5 font-mono text-[11px] text-blue-400 truncate">
                      {getPortalUrl(createdLink)}
                    </code>
                    <button
                      onClick={() => onCopyToClipboard(getPortalUrl(createdLink))}
                      className="p-1.5 bg-secondary hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      data-testid="copy-url-btn"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Access Code</label>
                  <div className="flex items-center gap-2 mt-0.5">
                    <code className="bg-card border border-primary/30 rounded-sm px-4 py-2 font-mono text-lg text-primary tracking-[0.4em] font-bold">
                      {createdLink.access_code}
                    </code>
                    <button
                      onClick={() => onCopyToClipboard(createdLink.access_code)}
                      className="p-1.5 bg-secondary hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors shrink-0"
                      data-testid="copy-code-btn"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {copiedCode && (
                  <span className="text-[10px] text-emerald-400 font-mono">Copied to clipboard</span>
                )}
                <p className="text-[10px] text-muted-foreground">Send the URL and access code to your client. They can view drawings and download the delivery package.</p>
              </div>
            </div>
          )}

          {/* Create new link form */}
          <div className="bg-card border border-border rounded-sm p-4 mb-5">
            <p className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground mb-3">Generate New Link</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Client Name</label>
                <input
                  value={newLink.client_name}
                  onChange={(e) => onNewLinkChange({ ...newLink, client_name: e.target.value })}
                  data-testid="share-client-name"
                  className="w-full bg-card border border-border focus:border-primary rounded-sm px-2 py-1.5 font-mono text-xs text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                  placeholder="Acme Corp"
                />
              </div>
              <div>
                <label className="block text-[9px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Client Email</label>
                <input
                  value={newLink.client_email}
                  onChange={(e) => onNewLinkChange({ ...newLink, client_email: e.target.value })}
                  data-testid="share-client-email"
                  className="w-full bg-card border border-border focus:border-primary rounded-sm px-2 py-1.5 font-mono text-xs text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                  placeholder="client@acme.com"
                />
              </div>
              <div>
                <label className="block text-[9px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Note</label>
                <input
                  value={newLink.note}
                  onChange={(e) => onNewLinkChange({ ...newLink, note: e.target.value })}
                  data-testid="share-note"
                  className="w-full bg-card border border-border focus:border-primary rounded-sm px-2 py-1.5 font-mono text-xs text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                  placeholder="Phase 2 drawings"
                />
              </div>
              <div>
                <label className="block text-[9px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Expires In</label>
                <select
                  value={newLink.expires_in_days}
                  onChange={(e) => onNewLinkChange({ ...newLink, expires_in_days: parseInt(e.target.value) })}
                  data-testid="share-expiry"
                  className="w-full bg-card border border-border focus:border-primary rounded-sm px-2 py-1.5 font-mono text-xs text-foreground outline-none"
                >
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>
            </div>
            <button
              onClick={onCreateLink}
              disabled={creatingLink}
              data-testid="create-share-link-btn"
              className="flex items-center gap-2 border-l-2 border-blue-500 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 font-heading font-bold uppercase tracking-wider text-xs h-9 px-4 transition-colors disabled:opacity-50 mt-3"
            >
              <Link2 className="w-3.5 h-3.5" />
              {creatingLink ? 'Generating...' : 'Generate Link & Code'}
            </button>
          </div>

          {/* Existing links */}
          {shareLinks.length > 0 && (
            <div>
              <p className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground mb-2">Active Links</p>
              <div className="space-y-2">
                {shareLinks.map((link) => (
                  <div
                    key={link.id}
                    className={`bg-card border rounded-sm p-3 ${link.revoked ? 'border-rose-900/50 opacity-60' : 'border-border'}`}
                    data-testid={`share-link-${link.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Shield className="w-3 h-3 text-blue-400" />
                        <span className="font-mono text-xs text-foreground">{link.client_name || 'Unnamed'}</span>
                        <code className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono text-primary">{link.access_code}</code>
                      </div>
                      {!link.revoked ? (
                        <button
                          onClick={() => onRevokeLink(link.id)}
                          className="p-1 text-muted-foreground hover:text-rose-400 transition-colors"
                          data-testid={`revoke-link-${link.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <span className="text-[10px] font-mono text-rose-500">REVOKED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
                      {link.client_email && <span>{link.client_email}</span>}
                      <span>Views: {link.access_count}</span>
                      <span>Expires: {new Date(link.expires_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
